import * as React from "react";
import {
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import type { LocationDto, LocationType } from "../../models/Locations";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import ErrorSummary from "../ErrorHandler/ErrorSummary";
import { buildTree, flattenWithLevel, formatIndentedLabel, getPath, isSameOrDescendant } from "./locationTree.utils";
import { getApiErrorMessage } from "./apiError.utils";
import type { LocationInput } from "./useLocationsAdmin";

export type FormTarget = { mode: "create"; parentId: number | null } | { mode: "edit"; id: number };

type LocationDetailsPanelProps = {
    target: FormTarget | null;
    locations: LocationDto[];
    locationTypes: LocationType[];
    onCreate: (input: LocationInput) => Promise<LocationDto>;
    onUpdate: (id: number, input: LocationInput) => Promise<void>;
    onRequestDelete: (id: number) => void;
    onCancel: () => void;
    onSaved: (locationId: number) => void;
};

export default function LocationDetailsPanel({
    target,
    locations,
    locationTypes,
    onCreate,
    onUpdate,
    onRequestDelete,
    onCancel,
    onSaved,
}: LocationDetailsPanelProps) {
    const { errors, handleError, clearErrors, validateRequired } = useErrorHandler();
    const [locationName, setLocationName] = React.useState("");
    const [locationTypeId, setLocationTypeId] = React.useState<number>(0);
    const [parentId, setParentId] = React.useState<number | null>(null);
    const [saving, setSaving] = React.useState(false);

    const editingLocation = target?.mode === "edit" ? locations.find((l) => l.locationId === target.id) : undefined;

    // Reset the form whenever the target changes (new selection, add-child, add-root, or cancel).
    React.useEffect(() => {
        clearErrors();
        if (!target) {
            setLocationName("");
            setLocationTypeId(0);
            setParentId(null);
        } else if (target.mode === "create") {
            setLocationName("");
            setLocationTypeId(0);
            setParentId(target.parentId);
        } else if (editingLocation) {
            setLocationName(editingLocation.locationName);
            setLocationTypeId(editingLocation.locationTypeId);
            setParentId(editingLocation.parentLocationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target?.mode, target?.mode === "edit" ? target.id : target?.parentId]);

    const parentOptions = React.useMemo(() => {
        const flat = flattenWithLevel(buildTree(locations));
        if (target?.mode !== "edit") return flat;
        // An edited node can't become its own parent, or a descendant of itself.
        return flat.filter((o) => !isSameOrDescendant(locations, target.id, o.locationId));
    }, [locations, target]);

    const breadcrumbTrail = React.useMemo(() => {
        if (!target) return [];
        const anchorId = target.mode === "edit" ? target.id : target.parentId;
        return getPath(locations, anchorId ?? null);
    }, [target, locations]);

    if (!target) {
        return (
            <Paper variant="outlined" sx={{ p: 4, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary" align="center">
                    Select a location on the left to edit it, or use the + buttons to add a new one.
                </Typography>
            </Paper>
        );
    }

    const isEditing = target.mode === "edit";

    const handleSave = async () => {
        clearErrors();
        let isValid = true;
        if (!validateRequired(locationName, "Location", "A location name is required.")) isValid = false;
        if (locationTypeId === 0) {
            handleError({
                code: "Location Type",
                message: "Location type must be set.",
                details: "Please select a location type.",
            });
            isValid = false;
        }
        if (!isValid) return;

        const input: LocationInput = {
            locationName: locationName.trim(),
            locationTypeId,
            parentLocationId: parentId,
        };

        setSaving(true);
        try {
            if (isEditing) {
                await onUpdate(target.id, input);
                onSaved(target.id);
            } else {
                const created = await onCreate(input);
                onSaved(created.locationId);
            }
        } catch (e: any) {
            handleError({
                code: "Save failed",
                message: getApiErrorMessage(e, "Unable to save this location."),
                details: "Please try again.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
            <Breadcrumbs sx={{ mb: 1 }} aria-label="Location path">
                <HomeOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                {breadcrumbTrail.map((n) => (
                    <Typography key={n.locationId} variant="body2" color="text.secondary">
                        {n.locationName}
                    </Typography>
                ))}
                {!isEditing && (
                    <Typography variant="body2" color="primary">
                        New location
                    </Typography>
                )}
            </Breadcrumbs>

            <Typography variant="h6" gutterBottom>
                {isEditing ? `Edit "${editingLocation?.locationName ?? ""}"` : "Create New Location"}
            </Typography>

            <ErrorSummary errors={errors} title="Location Detail Errors" />

            <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                    fullWidth
                    label="Location Name"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    size="small"
                    autoFocus
                />

                <FormControl fullWidth size="small">
                    <InputLabel id="location-type-label">Location Type</InputLabel>
                    <Select
                        labelId="location-type-label"
                        value={locationTypeId || ""}
                        onChange={(e) => setLocationTypeId(Number(e.target.value))}
                        label="Location Type"
                    >
                        {locationTypes.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Autocomplete
                    size="small"
                    options={parentOptions}
                    getOptionLabel={(o) => formatIndentedLabel(o.locationName, o.level).trim()}
                    isOptionEqualToValue={(o, v) => o.locationId === v.locationId}
                    value={parentOptions.find((o) => o.locationId === parentId) ?? null}
                    onChange={(_, value) => setParentId(value ? value.locationId : null)}
                    renderInput={(params) => <TextField {...params} label="Parent Location" placeholder="None (top level)" />}
                />

                <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {isEditing ? "Update" : "Create"}
                    </Button>
                    {isEditing && (
                        <Button variant="outlined" color="error" onClick={() => onRequestDelete(target.id)} disabled={saving}>
                            Remove
                        </Button>
                    )}
                    <Button variant="text" onClick={onCancel} disabled={saving}>
                        Cancel
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
}
